import { cn } from "@/lib/utils/cn";
import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import * as React from "react";

const TooltipProvider = BaseTooltip.Provider;

const TooltipTrigger = ({ ...props }: React.ComponentProps<typeof BaseTooltip.Trigger>) => {
  return <BaseTooltip.Trigger data-slot="tooltip-trigger" {...props} />;
};

type TooltipContentProps =
  & React.ComponentProps<typeof BaseTooltip.Popup>
  & Pick<
    React.ComponentProps<typeof BaseTooltip.Positioner>,
    "side" | "align" | "sideOffset" | "alignOffset"
  >;

const TooltipContent = ({
  className,
  side = "top",
  align = "center",
  sideOffset = 4,
  alignOffset,
  ...props
}: TooltipContentProps) => (
  <BaseTooltip.Portal>
    <BaseTooltip.Positioner
      className="z-50"
      side={side}
      align={align}
      sideOffset={sideOffset}
      {...(alignOffset !== undefined && { alignOffset })}
    >
      <BaseTooltip.Popup
        data-slot="tooltip-content"
        className={cn(
          "origin-[var(--transform-origin)] overflow-hidden rounded-md border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-md transition-[transform,scale,opacity] data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0",
          className,
        )}
        {...props}
      />
    </BaseTooltip.Positioner>
  </BaseTooltip.Portal>
);

type TooltipProps =
  & Omit<React.ComponentProps<typeof BaseTooltip.Root>, "delay">
  & { delayDuration?: number; };

const TooltipBase = ({ delayDuration = 300, children, ...props }: TooltipProps) => (
  <BaseTooltip.Provider delay={delayDuration}>
    <BaseTooltip.Root {...props}>{children}</BaseTooltip.Root>
  </BaseTooltip.Provider>
);

export const Tooltip = Object.assign(TooltipBase, {
  Root: BaseTooltip.Root,
  Trigger: TooltipTrigger,
  Content: TooltipContent,
  Provider: TooltipProvider,
});
